import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

import { isRecord } from '@/helpers/isRecord'
import { firebaseConfig, getFirebaseApp } from '@/services/Firebase/firebaseApp'
import { NativeBridge, NotificationPermissionStatus, NotificationTappedPayload } from '@/types'

import { isNotificationTappedPayload } from './isNotificationTappedPayload'

const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

const buildServiceWorkerUrl = (): string => {
    const params = new URLSearchParams({
        apiKey: firebaseConfig.apiKey ?? '',
        authDomain: firebaseConfig.authDomain ?? '',
        projectId: firebaseConfig.projectId ?? '',
        messagingSenderId: firebaseConfig.messagingSenderId ?? '',
        appId: firebaseConfig.appId ?? '',
    })

    return `${SERVICE_WORKER_PATH}?${params.toString()}`
}

const waitForActiveServiceWorker = (registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> => {
    if (registration.active) {
        return Promise.resolve(registration)
    }

    const worker = registration.installing ?? registration.waiting

    if (! worker) {
        return navigator.serviceWorker.ready
    }

    return new Promise((resolve) => {
        const onStateChange = (): void => {
            if (worker.state === 'activated') {
                worker.removeEventListener('statechange', onStateChange)
                resolve(registration)
            }
        }

        worker.addEventListener('statechange', onStateChange)
    })
}

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null

const getServiceWorkerRegistration = (): Promise<ServiceWorkerRegistration> => {
    if (! registrationPromise) {
        registrationPromise = navigator.serviceWorker.register(buildServiceWorkerUrl())
            .then(waitForActiveServiceWorker)
    }

    return registrationPromise
}

const isBrowserPushCapable = (): boolean => (
    typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
)

export const createFirebaseWebPushBridge = (): NativeBridge => {
    const available = isBrowserPushCapable()
    const tappedHandlers = new Set<(payload: NotificationTappedPayload) => void>()

    if (available) {
        navigator.serviceWorker.addEventListener('message', (event) => {
            const message: unknown = event.data

            if (
                isRecord(message)
                && message.type === 'event'
                && message.name === 'notificationTapped'
                && isNotificationTappedPayload(message.payload)
            ) {
                const { payload } = message
                tappedHandlers.forEach(handler => handler(payload))
            }
        })

        isSupported().then((supported) => {
            if (! supported) {
                return
            }

            const messaging = getMessaging(getFirebaseApp())

            onMessage(messaging, (payload) => {
                const { notification, data } = payload

                if (! notification) {
                    return
                }

                const shown = new Notification(notification.title ?? '', {
                    body: notification.body,
                    data,
                })

                shown.onclick = (): void => {
                    shown.close()
                    tappedHandlers.forEach(handler => handler({ ...data, route: data?.route ?? '/' }))
                }
            })
        }).catch(error => console.error(error))
    }

    return {
        isAvailable: available,
        getNotificationPermissionStatus: async (): Promise<NotificationPermissionStatus> => (
            available ? Notification.permission : 'default'
        ),
        requestNotificationPermission: async (): Promise<NotificationPermissionStatus> => (
            available ? Notification.requestPermission() : 'denied'
        ),
        getPushToken: async (): Promise<string | null> => {
            if (! available || Notification.permission !== 'granted') {
                return null
            }

            if (! await isSupported()) {
                return null
            }

            try {
                const registration = await getServiceWorkerRegistration()
                const messaging = getMessaging(getFirebaseApp())

                // @TODO: Refactor this once Stream switches to the new FID token format
                return await getToken(messaging, {
                    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
                    serviceWorkerRegistration: registration,
                })
            } catch (error) {
                console.error(error)
                return null
            }
        },
        onNotificationTapped: (handler) => {
            tappedHandlers.add(handler)

            return () => {
                tappedHandlers.delete(handler)
            }
        },
    }
}
